import {
  BehaviorSubject,
  Subject,
  catchError,
  debounceTime,
  filter,
  finalize,
  switchMap,
  takeUntil,
  tap,
  throwError,
} from 'rxjs';
import { AdminsApi } from './../../../hickory-api/api/admins.service';
import { Component, computed, OnDestroy, OnInit, signal } from '@angular/core';
import { AdminProfile, AdminSummary } from 'src/app/hickory-api';
import { ApiError } from '../../model/utility';
import { AutocompleteControlRenderer } from '@jsonforms/angular-material';
import { JsonFormsAngularService } from '@jsonforms/angular';
import { MatAutocompleteSelectedEvent } from '@angular/material/autocomplete';

@Component({
  selector: 'jsonforms-admin-id',
  styleUrl: './jsonforms-admin-id-renderer.component.scss',
  templateUrl: './jsonforms-admin-id-renderer.component.html',
  standalone: false
})
export class JsonformsAdminIdRendererComponent extends AutocompleteControlRenderer implements OnInit, OnDestroy {
  admins = new BehaviorSubject<AdminProfile[]>(null);
  loading = new BehaviorSubject<boolean>(false);
  adminMap = new Map<number, AdminSummary>();

  
  private readonly destroy$ = new Subject<void>();
  
  selected = signal<AdminSummary | null>(null);
  currentSelectedAdmin = computed(() => {
    return this.selected() ? `${this.selected().firstName} + ${this.selected().lastName}` : null;
  });

  constructor(private readonly adminsApi: AdminsApi, public readonly jsonFormsService: JsonFormsAngularService) {
    super(jsonFormsService);
  }

  ngOnInit(): void {
    super.ngOnInit();

    this.form.valueChanges
      .pipe(
        filter((value: string) => value?.length >= 2),
        debounceTime(300),
        tap(() => this.loading.next(true)),
        switchMap((value) => this.getAdmins(value)),
        takeUntil(this.destroy$)
      )
      .subscribe();
  }

  ngOnDestroy(): void {
    super.ngOnDestroy();
    this.destroy$.next();
    this.destroy$.complete();
  }

  onChange(ev: unknown): void {
    if (isNaN(+ev)) {
      return;
    }

    super.onChange(ev);
  }

  onSelect(ev: MatAutocompleteSelectedEvent): void {
    super.onSelect(ev);
    if (!ev.option.value) {
      this.selected = null;
      return;
    }

    this.selected.set(this.adminMap.get(ev.option.value));
  }

  getAdminName(adminId: number) {
    const admin = this.adminMap.get(adminId);
    if (!admin) {
      return 'Unknown';
    }

    return `${admin.firstName} + ${admin.lastName}`;
  }

  getAdmins(input: string) {
    return this.adminsApi
      .getAdminUsers(
        1,
        25,
        undefined,
        undefined,
        input,
        undefined,
        undefined,
        undefined,
        undefined,
        this.uischema.options?.['tenantId']
      )
      .pipe(
        catchError((err: ApiError) => {
          this.error = err.message ?? err.error?.status?.errorMessage;
          return throwError(() => err);
        }),
        tap((result) => {
          result.items.forEach((admin) => {
            this.adminMap.set(admin.id, admin);
          });

          this.options = result.items.map((admin) => admin.id.toString());
        }),
        finalize(() => this.loading.next(false)),
        takeUntil(this.destroy$)
      );
  }
}
